import { inject } from 'aurelia';
import { route, IRouter, IRouterEvents, NavigationEndEvent, RouteNode } from '@aurelia/router';
import { IDisposable } from '@aurelia/kernel';
import { I18N } from '@aurelia/i18n';
import './prototypes/array';
import { LanguageResolver } from './infrastructure/language-resolver';
import { Popover as BootstrapPopover } from 'bootstrap';
import { UserContext } from './contexts/user-context';
import { CreateSection} from './modules/edit/create-section';
import { LoginPage } from './modules/login/login-page';
import { PresentSection } from './modules/present/present-section';
import { About } from './modules/about/about';
import { MyEvents } from './modules/my-events/my-events';
import { Invites } from './modules/invites/invites';
import { MapView } from './modules/map/map-view';
import { AccountPage } from './modules/account/account-page';
import { OnboardingPage } from './modules/onboarding/onboarding-page';
import { RegistrationPage } from './modules/registration/registration-page';
import { ProjectModelService } from './services/project-model-service';

const tr = (node: RouteNode, key: string): string => node.context.container.get(I18N).tr(key);

@route({
  title: 'sammandrag.se',
  routes: [
    { path: '', redirectTo: 'create' },
    { id: 'create', path: 'create', component: CreateSection, title: (node: RouteNode): string => tr(node, 'ui:nav_create') },
    { id: 'login', path: 'login', component: LoginPage, title: (node: RouteNode): string => tr(node, 'ui:login_title') },
    { id: 'present', path: 'present', component: PresentSection, title: (node: RouteNode): string => tr(node, 'ui:nav_present') },
    { id: 'registration', path: 'registration/:code', component: RegistrationPage, title: 'registration' },
    { id: 'about', path: 'about', component: About, title: (node: RouteNode): string => tr(node, 'ui:nav_about') },
    { id: 'my-events', path: 'my-events', component: MyEvents, title: (node: RouteNode): string => tr(node, 'ui:nav_my_events') },
    { id: 'invites', path: 'invites', component: Invites, title: (node: RouteNode): string => tr(node, 'ui:nav_invites') },
    { id: 'map', path: 'map', component: MapView, title: (node: RouteNode): string => tr(node, 'ui:nav_map') },
    { id: 'account', path: 'account', component: AccountPage, title: (node: RouteNode): string => tr(node, 'ui:account_title') },
    { id: 'onboarding', path: 'onboarding', component: OnboardingPage, title: (node: RouteNode): string => tr(node, 'ui:onboarding_title') },
  ],
  fallback: 'create',
})
@inject(IRouter, IRouterEvents, LanguageResolver, ProjectModelService, UserContext)
export class Shell {

  private _navEndSubscription: IDisposable = null;

  constructor(
    private readonly router: IRouter,
    private readonly routerEvents: IRouterEvents,
    private readonly _languageResolver: LanguageResolver,
    private readonly _projectModelService: ProjectModelService,
    private readonly _userContext: UserContext,
  ) {
  }

  public async loading(): Promise<void> {
    // On a page reload inside create/…/<eventId>/… preload the project cache
    // so child routes never have to handle the "state lost" case themselves.
    const createSegment = window.location.pathname.match(/\/create\/[^/]+\/([^/]+)/i);
    if (createSegment != null && this._projectModelService.currentModel == null) {
        try {
            await this._projectModelService.loadByEventId(createSegment[1], false);
        } catch {
            // Preload failed (e.g. invalid/expired event ID) — redirect to start
            await this.router.load('create');
        }
    }
  }

  public get homeUrl(): string {
    return `${window.location.origin}/${this._languageResolver.currentLanguage}/`;
  }

  public get baseUrl(): string {
    return window.location.origin;
  }

  public get language(): string {
    return this._languageResolver.currentLanguage;
  }

  public binding(): void {
    this._setupPopovers();
    // Replaces the v1 'postcomplete' pipeline step.
    this._navEndSubscription = this.routerEvents.subscribe('au:router:navigation-end', (_event: NavigationEndEvent) => {
      // Direct assignment bypasses CSS scroll-behavior:smooth on all iOS versions.
      // window.scrollTo({ behavior:'instant' }) only works on Safari 15.4+.
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0; // fallback for older Safari

      // Enforce onboarding for all routes until terms are accepted.
      if (this._userContext.isLoggedIn &&
          this._userContext.userEntity.termsAcceptedAt == null &&
          !window.location.pathname.match(/\/(onboarding)$/)) {
        void this.router.load('onboarding');
      }
    });
  }

  public unbinding(): void {
    this._navEndSubscription?.dispose();
  }

  private _setupPopovers(): void {
    document.documentElement.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.popover') && !target.closest('[data-bs-toggle="popover"]')) {
        const popoverTriggers = document.querySelectorAll('[data-bs-toggle="popover"]');
        popoverTriggers.forEach(trigger => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          const popoverInstance = BootstrapPopover.getInstance(trigger as HTMLElement);
          if (popoverInstance) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            popoverInstance.hide();
          }
        });
      }
    });
  }
}
